/* global Browser */

import isEmail from 'validator/lib/isEmail';
import equals from 'validator/lib/equals';
import matches from 'validator/lib/matches';
import dom from '../lib/dom';

import { addListener } from '../lib/event';

const invalidClass = 'is-invalid';
const validClass = 'is-valid';
const pwLength = 8;

/**
 * Validator does validation - who would've thought that!
 * We use npm validator as the base component, and tweak this to match our requirements.
 *
 * @type {Class}
 * @author Steffen Dietz (sd@taikonauten.com)
 */
export default class Validator {

  constructor($el) {
    this.$el = $el;
    this.$parent = $el.parentNode;

    // parent form, injected by the form
    this.form = null;

    this.msBrowsers = !!(Browser.isIE || Browser.browserName === 'Edge');

    // equals validation target field
    this.$target = null;
    this.valid = false;
    this.hideValidation = true;

    // get constraint, either a keyword or a regex
    this.constraint = this.$el.getAttribute('data-validator');

    // is this a known constraint or a regex
    if (this.getHandlerWithConstraint(this.constraint) === undefined) {

      try {
        this.regex = new RegExp(this.constraint);
        this.constraint = 'regex';
      } catch (err) {
        // exit if this function does not exist or is not a valid regex
        throw new Error('[VALIDATOR] function is not defined', err);
      }
    }
    if (this.$el.type === 'password' && !this.msBrowsers) {

      const shIndicator = dom.create('i', {
        class: 'password-visibility i-show-purchase hide'
      }, []);

      addListener(shIndicator, 'click', e => {
        e.preventDefault();

        if (e.target.classList.contains('i-show-purchase')) {

          this.$el.type = 'text';
          e.target.classList.remove('i-show-purchase');
          e.target.classList.add('i-hide-purchase');

        } else if (e.target.classList.contains('i-hide-purchase')) {

          this.$el.type = 'password';
          e.target.classList.remove('i-hide-purchase');
          e.target.classList.add('i-show-purchase');
        }
      });

      this.$el.parentNode.appendChild(shIndicator);
    }

    // bind our handler to all events
    addListener(this.$el, 'focus', this.onFocus.bind(this));
    addListener(this.$el, 'keyup', this.onChange.bind(this));
    addListener(this.$el, 'blur', this.onBlur.bind(this));

    // initial revalidate
    this.revalidate();
  }

  onFocus() {
    this.revalidate();
  }

  onChange(e) {
    this.revalidate();
    this.setHideValidation(true);

    if (!this.msBrowsers) {

      if (e.target.type === 'password' && e.target.value !== '') {
  
        e.target.parentNode.querySelector('.password-visibility').classList.remove('hide');
      } else if (e.target.type === 'password' && e.target.value === '' && !e.target.parentNode.querySelector('.password-visibility').classList.contains('hide')) {
  
        e.target.parentNode.querySelector('.password-visibility').classList.add('hide');
      }
    }
  }

  onBlur() {
    this.revalidate();
    this.setHideValidation(false);
  }

  revalidate() {

    if (this.$el.disabled || this.$el.readonly || null === this.$el.offsetParent) {

      this.valid = true;
      return;
    }

    const value = this.$el.value;
    const valid = this.validate(this.constraint, value);
    this.setValid(valid);
  }

  getHandlerWithConstraint (constraint) {
    const method = 'validate' +
      constraint.charAt(0).toUpperCase() +
      constraint.slice(1);
    return this[method];
  }

  validate(constraint, value) {
    const handler = this.getHandlerWithConstraint(constraint);
    return handler.call(this, value);
  }

  validateEmail(value) {
    return isEmail(value);
  }

  validateEquals(value) {

    // empty values are not valid
    if (value === '') {
      return false;
    }

    // find target if not already done yet
    if (this.$target === null) {
      if (this.form !== null) {
        this.$target = this.form.$el.querySelector('.equals-target input');
      }
    }

    if (this.$target === null) {
      // no target found
      return true;
    }

    if (this.$target.value.length >= pwLength && this.$target.type === 'password') {

      return equals(value, this.$target.value);
    } else if (this.$target.type !== 'password') {

      return equals(value, this.$target.value);
    }
  }

  validateRegex(value) {
    return matches(value, this.regex);
  }

  validateNotEmpty(value) {
    return value !== '';
  }

  isValid() {
    return this.valid;
  }

  setHideValidation(hideValidation) {
    if (this.hideValidation !== hideValidation) {
      this.hideValidation = hideValidation;
      this.setValid(this.valid, true);
    }
    return this;
  }

  setValid(valid, force = false) {

    if (force || this.valid !== valid) {
      this.valid = valid;

      this.$parent.classList.remove(invalidClass);
      this.$parent.classList.remove(validClass);

      if (valid) {
        this.$parent.classList.add(validClass);
      } else if (!this.hideValidation) {
        this.$parent.classList.add(invalidClass);
      }
    }
    return this;
  }

  setForm(form) {
    this.form = form;
    return this;
  }
}

